import { v4 } from "uuid";

import {
  QuantityButtonsWidgetDto,
  ResolvedQuantityButtonsDeviceConfig,
} from "@intelligems/ig-types";
import { merge } from "@intelligems/ig-utils";
import {
  resolveDesktopQuantityButtonsConfig,
  resolveMobileQuantityButtonsConfig,
} from "@intelligems/ig-widgets/QuantityButtonsView/resolveQuantityButtonsConfig";
import { createSelector } from "@reduxjs/toolkit";
import { RootState, useSelector, useSelectorFactory } from "@src/store";
import { getVariationOfferProducts } from "@src/store/slices/offerProducts/selectors/getVariationOfferProducts";
import { getVariationOffer } from "@src/store/slices/offers/selectors";
import { getQuantityButtonsInterfaceForVariation } from "@src/store/slices/userInterfaces/selectors";
import { getQuantityButtons, selectedWidgets } from "@src/store/slices/widgets";
import { QuantityButtonsConfig } from "@src/store/slices/widgets/types";
import { filterWidgets } from "@src/store/slices/widgets/util";

export const getQuantityButtonsList = createSelector(
  selectedWidgets,
  (widgets) =>
    filterWidgets<QuantityButtonsWidgetDto>(widgets, { quantityButtons: true })
);

export const getLibraryQuantityButtonsList = createSelector(
  getQuantityButtonsList,
  (widgets) => widgets.map((w) => merge(w, getDefaultQuantityButtonsWidget()))
);

export const getUserInterfaceQuantityButtons = (variationId: string) =>
  createSelector(
    getQuantityButtonsInterfaceForVariation(variationId),
    (userInterface) => {
      if (!userInterface?.widget) return undefined;

      const desktopStyles = userInterface.styleOverrides
        ? merge(
            userInterface.widget.config?.desktop?.styles || {},
            userInterface.styleOverrides
          )
        : userInterface.widget.config?.desktop?.styles;

      const mobileStyles = userInterface.styleOverrides
        ? merge(
            userInterface.widget.config?.mobile?.styles || {},
            userInterface.styleOverrides
          )
        : userInterface.widget.config?.mobile?.styles;

      return merge(userInterface.widget, {
        config: {
          desktop: {
            variables: userInterface.desktopVariables,
            styles: desktopStyles,
          },
          mobile: {
            variables: userInterface.mobileVariables,
            styles: mobileStyles,
          },
        },
      });
    }
  );
export const useUserInterfaceQuantityButtons = (variationId: string) =>
  useSelectorFactory(getUserInterfaceQuantityButtons, variationId);

export const getUserInterfaceDrawerQuantityButtons = (variationId: string) =>
  createSelector(
    getQuantityButtons,
    getQuantityButtonsInterfaceForVariation(variationId),
    (widget, userInterface) => {
      if (!widget || !userInterface) return undefined;

      return merge(widget, {
        config: {
          desktop: {
            variables: userInterface.desktopVariables,
          },
          mobile: {
            variables: userInterface.mobileVariables,
          },
        },
      });
    }
  );
export const useUserInterfaceDrawerQuantityButtons = (variationId: string) =>
  useSelectorFactory(getUserInterfaceDrawerQuantityButtons, variationId);

export const isQuantityButtonsEnabled =
  (variationId: string) => (state: RootState) => {
    const offer = getVariationOffer(variationId)(state);
    const variationOfferProducts =
      getVariationOfferProducts(variationId)(state);
    return (
      offer &&
      offer.tiers.length > 1 &&
      variationOfferProducts.length > 0 &&
      offer.unitType === "unit"
    );
  };

export const getResolvedDesktopQuantityButtonsConfig = createSelector(
  getQuantityButtons,
  (widget) => {
    if (!widget) return undefined;
    return resolveDesktopQuantityButtonsConfig(widget.config);
  }
);

export const getResolvedMobileQuantityButtonsConfig = createSelector(
  getQuantityButtons,
  (widget) => {
    if (!widget) return undefined;
    return resolveMobileQuantityButtonsConfig(widget.config);
  }
);

export const areDeviceQuantityButtonsDifferent = createSelector(
  getResolvedDesktopQuantityButtonsConfig,
  getResolvedMobileQuantityButtonsConfig,
  (desktop, mobile) =>
    JSON.stringify({
      variant: desktop?.variant,
      variables: desktop?.variables,
      styles: desktop?.styles,
    }) !==
    JSON.stringify({
      variant: mobile?.variant,
      variables: mobile?.variables,
      styles: mobile?.styles,
    })
);

function getDefaultQuantityButtonsWidget() {
  return {
    config: {
      desktop: getDefaultQuantityButtonsDevice(),
      mobile: getDefaultQuantityButtonsDevice(),
    },
  };
}

function getDefaultQuantityButtonsDevice() {
  return {
    variables: {
      buttons: [
        {
          id: v4(),
          quantity: 1,
          unitName: "units",
        },
        {
          id: v4(),
          quantity: 3,
          unitName: "units",
        },
        {
          id: v4(),
          quantity: 5,
          unitName: "units",
          extraMessage: "Most popular",
        },
      ],
    },
  };
}
